import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { toast } from 'sonner';
import {
  Plus, Trash2, ChevronLeft, ChevronRight, ChevronDown, Indent, Outdent,
  GripVertical, BarChart3, ZoomIn, ZoomOut, CalendarPlus, Package, FileDown, ListTree, CalendarClock
} from 'lucide-react';

const API_URL = '';
const ROW_HEIGHT = 26;
const HEADER_HEIGHT = 36;

function toLocalDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function addDays(ds, n) { const d=new Date(ds+'T00:00:00'); d.setDate(d.getDate()+n); return toLocalDateStr(d); }
function diffDays(a, b) { return Math.round((new Date(b+'T00:00:00')-new Date(a+'T00:00:00'))/(864e5)); }
function countWD(s, e) {
  // All calendar days inclusive (weekends count as working days)
  return diffDays(s, e) + 1;
}
function addWD(s, wd) {
  // Add calendar days (weekends included)
  if (wd <= 0) return s;
  return addDays(s, wd - 1);
}
function genTimeline(start, days) {
  const arr=[], d=new Date(start+'T00:00:00');
  for(let i=0;i<days;i++){
    arr.push({ date:toLocalDateStr(d), day:d.getDate(), month:d.getMonth(), year:d.getFullYear(),
      dow:d.getDay(), mn:d.toLocaleDateString('en-US',{month:'short'}) });
    d.setDate(d.getDate()+1);
  } return arr;
}

// Check if task is behind schedule
function isBehind(task) {
  if (!task.start_date || !task.end_date || !task.working_days || task.working_days <= 0) return false;
  const today = toLocalDateStr(new Date());
  if (today <= task.start_date) return false; // hasn't started yet
  if (today >= task.end_date) return task.progress < 100; // past due and not complete
  const elapsedWD = countWD(task.start_date, today);
  const expectedPct = (elapsedWD / task.working_days) * 100;
  return task.progress < expectedPct - 5; // 5% buffer
}

export default function GanttChart({ chartId, onBack, user, onCreateCalTask, onCreatePO }) {
  const [chart, setChart] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [newTaskName, setNewTaskName] = useState('');
  const [dayWidth, setDayWidth] = useState(32);
  const [colWidths, setColWidths] = useState({ name: 120, start: 48, wd: 28, end: 48, pct: 28 });
  const [dragIdx, setDragIdx] = useState(null);
  const [exporting, setExporting] = useState(false);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API_URL}/api/gantt/${chartId}/export/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(chart?.project_name || 'schedule').replace(/[^a-z0-9]/gi, '_')}_schedule.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export schedule PDF');
    } finally {
      setExporting(false);
    }
  };
  const [dropIdx, setDropIdx] = useState(null);
  const [collapsedIds, setCollapsedIds] = useState(new Set());
  const [importOpen, setImportOpen] = useState(false);
  const [importProjects, setImportProjects] = useState([]);
  const [importSource, setImportSource] = useState('');
  const [importing, setImporting] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveDate, setMoveDate] = useState(null);
  const [moving, setMoving] = useState(false);
  const chartAreaRef = useRef(null);
  const dragRef = useRef(null);
  const colResizeRef = useRef(null);
  const saveTimeoutRef = useRef(null);

  const loadChart = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/gantt/${chartId}`);
      setChart(res.data); setTasks(res.data.tasks || []);
    } catch { toast.error('Failed to load chart'); }
    finally { setLoading(false); }
  }, [chartId]);

  useEffect(() => { loadChart(); }, [loadChart]);

  const autoSave = useCallback((ut) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        const res = await axios.put(`${API_URL}/api/gantt/${chartId}`, { tasks: ut });
        setTasks(res.data.tasks);
        setChart(p => ({ ...p, project_start: res.data.project_start, project_end: res.data.project_end,
          project_working_days: res.data.project_working_days, project_progress: res.data.project_progress }));
      } catch { toast.error('Failed to save'); }
      finally { setSaving(false); }
    }, 600);
  }, [chartId]);

  // Auto-compute parent task dates from children
  const computeParentDates = useCallback((taskList) => {
    const u = taskList.map(t => ({ ...t }));
    // Process from bottom up: find parents and compute from their direct children
    for (let i = u.length - 1; i >= 0; i--) {
      // Check if this task has children (next task has higher level)
      const childIndices = [];
      for (let j = i + 1; j < u.length; j++) {
        if (u[j].level > u[i].level) childIndices.push(j);
        else break;
      }
      if (childIndices.length === 0) continue; // leaf task, skip

      // Compute from dated children
      const datedChildren = childIndices.filter(ci => u[ci].start_date && u[ci].end_date);
      if (datedChildren.length > 0) {
        const minStart = datedChildren.reduce((m, ci) => (!m || u[ci].start_date < m) ? u[ci].start_date : m, null);
        const maxEnd = datedChildren.reduce((m, ci) => (!m || u[ci].end_date > m) ? u[ci].end_date : m, null);
        u[i].start_date = minStart;
        u[i].end_date = maxEnd;
        u[i].working_days = countWD(minStart, maxEnd);
        // Weighted progress from children
        let totalWD = 0, weightedProg = 0;
        datedChildren.forEach(ci => {
          const wd = u[ci].working_days || 0;
          if (wd > 0) { weightedProg += (u[ci].progress || 0) * wd; totalWD += wd; }
        });
        u[i].progress = totalWD > 0 ? Math.round(weightedProg / totalWD) : 0;
      }
    }
    return u;
  }, []);

  const updateTasks = useCallback((nt) => {
    const computed = computeParentDates(nt);
    setTasks(computed);
    autoSave(computed);
  }, [autoSave, computeParentDates]);

  const timeline = useMemo(() => {
    const today = toLocalDateStr(new Date());
    let mn = today, mx = addDays(today, 60);
    tasks.forEach(t => {
      if (t.start_date && t.start_date < mn) mn = t.start_date;
      if (t.end_date && t.end_date > mx) mx = t.end_date;
    });
    const ps = addDays(mn, -7), pe = addDays(mx, 14);
    const td = diffDays(ps, pe) + 1;
    return { dates: genTimeline(ps, td), startDate: ps, totalDays: td };
  }, [tasks]);

  const getIdx = useCallback((ds) => diffDays(timeline.startDate, ds), [timeline]);

  // Get indices of all children (subtasks nested under task at idx)
  const getChildIndices = (idx) => {
    const parentLevel = tasks[idx].level;
    const children = [];
    for (let i = idx + 1; i < tasks.length; i++) {
      if (tasks[i].level > parentLevel) children.push(i);
      else break;
    }
    return children;
  };

  const isParent = (idx) => {
    return idx < tasks.length - 1 && tasks[idx + 1]?.level > tasks[idx].level;
  };

  const toggleCollapse = (taskId) => {
    setCollapsedIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  // Build visible task list (hide children of collapsed parents)
  const visibleTasks = [];
  const skipUntilLevel = [];
  for (let i = 0; i < tasks.length; i++) {
    // Check if any ancestor is collapsed
    if (skipUntilLevel.length > 0 && tasks[i].level > skipUntilLevel[skipUntilLevel.length - 1]) {
      continue; // hidden
    }
    // Pop skip levels that don't apply anymore
    while (skipUntilLevel.length > 0 && tasks[i].level <= skipUntilLevel[skipUntilLevel.length - 1]) {
      skipUntilLevel.pop();
    }
    visibleTasks.push({ ...tasks[i], _origIdx: i });
    if (collapsedIds.has(tasks[i].id) && isParent(i)) {
      skipUntilLevel.push(tasks[i].level);
    }
  }

  // Task operations
  const addTask = (name) => {
    const n = (name || '').trim() || 'New Task';
    updateTasks([...tasks, { id: crypto.randomUUID(), name: n, start_date: null, working_days: null, end_date: null, progress: 0, level: 0 }]);
    setNewTaskName('');
  };

  const openImport = async () => {
    setImportSource(''); setImportOpen(true);
    try { const r = await axios.get(`${API_URL}/api/projects`); setImportProjects(r.data || []); }
    catch { toast.error('Failed to load projects'); }
  };

  const buildTasksFromBom = (data) => {
    let cats = data?.categories;
    if ((!cats || !cats.length) && data?.sections) cats = [{ name: 'GENERAL', sections: data.sections }];
    const out = [];
    const mk = (name, level) => ({ id: crypto.randomUUID(), name: name || '—', start_date: null, working_days: null, end_date: null, progress: 0, level });
    (cats || []).forEach(cat => {
      if (cat.name) out.push(mk(cat.name, 0));
      (cat.sections || []).forEach(sec => {
        out.push(mk([sec.item_no, sec.title].filter(Boolean).join('  ').trim() || 'SECTION', 1));
        (sec.items || []).forEach(it => {
          out.push(mk([it.item_no, it.scope].filter(Boolean).join('  ').trim() || 'Task', 2));
        });
      });
    });
    return out;
  };

  const doImport = async (mode) => {
    if (!importSource) { toast.error('Select a project'); return; }
    setImporting(true);
    try {
      const r = await axios.get(`${API_URL}/api/bom/${importSource}`);
      const newTasks = buildTasksFromBom(r.data);
      if (!newTasks.length) { toast.error('That project has no BOM scope to import'); setImporting(false); return; }
      updateTasks(mode === 'replace' ? newTasks : [...tasks, ...newTasks]);
      toast.success(`Imported ${newTasks.length} scope rows (${mode === 'replace' ? 'replaced schedule' : 'appended'})`);
      setImportOpen(false);
    } catch { toast.error('Failed to import scope from that project BOM'); }
    setImporting(false);
  };

  const scheduleStart = chart?.project_start || tasks.reduce((m, t) => (t.start_date && (!m || t.start_date < m)) ? t.start_date : m, null);

  const openMove = () => {
    setMoveDate(scheduleStart ? new Date(scheduleStart + 'T00:00:00') : new Date());
    setMoveOpen(true);
  };

  const moveSchedule = async () => {
    if (!moveDate) { toast.error('Pick a start date'); return; }
    if (!scheduleStart) { toast.error('No dated tasks to move — add dates to a task first'); return; }
    const newStart = toLocalDateStr(moveDate);
    if (newStart === scheduleStart) { toast.info('Schedule already starts on that date'); setMoveOpen(false); return; }
    setMoving(true);
    try {
      const res = await axios.put(`${API_URL}/api/gantt/${chartId}/shift?new_start=${newStart}`);
      setTasks(res.data.tasks);
      setChart(p => ({ ...p, project_start: res.data.project_start, project_end: res.data.project_end,
        project_working_days: res.data.project_working_days, project_progress: res.data.project_progress }));
      toast.success(`Entire schedule moved — now starts ${newStart}`);
      setMoveOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to move schedule'); }
    setMoving(false);
  };

  const deleteTask = (id) => {
    const idx = tasks.findIndex(t => t.id === id);
    if (idx === -1) return;
    const childIds = getChildIndices(idx);
    const idsToRemove = new Set([idx, ...childIds]);
    updateTasks(tasks.filter((_, i) => !idsToRemove.has(i)));
  };

  const indentTask = (idx) => {
    if (idx === 0) return;
    const c = tasks[idx], p = tasks[idx-1];
    if (c.level >= 2 || c.level > p.level) return;
    const children = getChildIndices(idx);
    const u = [...tasks];
    u[idx] = { ...c, level: c.level + 1 };
    children.forEach(ci => { u[ci] = { ...u[ci], level: u[ci].level + 1 }; });
    updateTasks(u);
  };

  const outdentTask = (idx) => {
    if (tasks[idx].level === 0) return;
    const children = getChildIndices(idx);
    const u = [...tasks];
    u[idx] = { ...tasks[idx], level: tasks[idx].level - 1 };
    children.forEach(ci => { u[ci] = { ...u[ci], level: u[ci].level - 1 }; });
    updateTasks(u);
  };

  const moveTask = (idx, dir) => {
    const children = getChildIndices(idx);
    const groupStart = idx;
    const groupEnd = children.length > 0 ? children[children.length - 1] : idx;
    const groupSize = groupEnd - groupStart + 1;

    if (dir === -1) {
      // Move group up: swap with the item above
      if (groupStart === 0) return;
      const u = [...tasks];
      const group = u.splice(groupStart, groupSize);
      u.splice(groupStart - 1, 0, ...group);
      updateTasks(u);
    } else {
      // Move group down: swap with the item (+ its children) below
      if (groupEnd >= tasks.length - 1) return;
      const nextIdx = groupEnd + 1;
      const nextChildren = getChildIndices(nextIdx);
      const nextEnd = nextChildren.length > 0 ? nextChildren[nextChildren.length - 1] : nextIdx;
      const nextSize = nextEnd - nextIdx + 1;
      const u = [...tasks];
      const nextGroup = u.splice(nextIdx, nextSize);
      u.splice(groupStart, 0, ...nextGroup);
      updateTasks(u);
    }
  };

  // Drag-to-reorder handlers
  const handleDragStart = (idx) => {
    setDragIdx(idx);
  };
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || idx === dragIdx) return;
    setDropIdx(idx);
  };
  const handleDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDropIdx(null); return; }
    const children = getChildIndices(dragIdx);
    const groupStart = dragIdx;
    const groupEnd = children.length > 0 ? children[children.length - 1] : dragIdx;
    const groupSize = groupEnd - groupStart + 1;
    const u = [...tasks];
    const group = u.splice(groupStart, groupSize);
    const targetIdx = idx > groupStart ? idx - groupSize : idx;
    u.splice(targetIdx, 0, ...group);
    updateTasks(u);
    setDragIdx(null);
    setDropIdx(null);
  };
  const handleDragEnd = () => { setDragIdx(null); setDropIdx(null); };

  const updateField = (id, field, value) => {
    const u = tasks.map(t => {
      if (t.id !== id) return t;
      const nt = { ...t, [field]: value };
      if (field === 'start_date' && nt.working_days > 0) nt.end_date = addWD(value, nt.working_days);
      else if (field === 'working_days' && nt.start_date) { nt.working_days = parseInt(value)||0; if(nt.working_days>0) nt.end_date = addWD(nt.start_date, nt.working_days); }
      else if (field === 'end_date' && nt.start_date) nt.working_days = countWD(nt.start_date, value);
      return nt;
    });
    updateTasks(u);
  };

  const startEdit = (tid, f, v) => { setEditingCell({ taskId: tid, field: f }); setEditValue(v ?? ''); };
  const commitEdit = () => {
    if (!editingCell) return;
    const { taskId, field } = editingCell;
    if (field === 'progress') updateField(taskId, field, Math.min(100, Math.max(0, parseInt(editValue)||0)));
    else if (field === 'working_days') updateField(taskId, field, parseInt(editValue)||0);
    else updateField(taskId, field, editValue);
    setEditingCell(null);
  };

  // Bar drag interactions
  const handleChartMouseDown = (e, taskId) => {
    if (e.button !== 0) return;
    const rect = chartAreaRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left + chartAreaRef.current.scrollLeft;
    const di = Math.floor(x / dayWidth);
    const ds = timeline.dates[di]?.date;
    if (!ds) return;
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    e.preventDefault();

    if (!task.start_date) {
      dragRef.current = { type: 'create', taskId, startDI: di, curDI: di };
    } else {
      const si = getIdx(task.start_date), ei = task.end_date ? getIdx(task.end_date) : si;
      if (Math.abs(di - si) <= 1) dragRef.current = { type: 'rs', taskId, oS: task.start_date, oE: task.end_date, ai: di };
      else if (Math.abs(di - ei) <= 1) dragRef.current = { type: 're', taskId, oS: task.start_date, oE: task.end_date, ai: di };
      else if (di >= si && di <= ei) dragRef.current = { type: 'mv', taskId, oS: task.start_date, oE: task.end_date, ai: di };
      else dragRef.current = { type: 'create', taskId, startDI: di, curDI: di };
    }
  };

  const handleMouseMove = useCallback((e) => {
    // Column resize
    if (colResizeRef.current) {
      const { col, startX, startW } = colResizeRef.current;
      const delta = e.clientX - startX;
      setColWidths(p => ({ ...p, [col]: Math.max(24, startW + delta) }));
      return;
    }
    // Bar drag
    if (!dragRef.current || !chartAreaRef.current) return;
    const rect = chartAreaRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left + chartAreaRef.current.scrollLeft;
    const di = Math.max(0, Math.min(Math.floor(x / dayWidth), timeline.dates.length - 1));
    const dr = dragRef.current;

    if (dr.type === 'create') {
      const s = Math.min(dr.startDI, di), en = Math.max(dr.startDI, di);
      const sd = timeline.dates[s]?.date, ed = timeline.dates[en]?.date;
      if (sd && ed) setTasks(p => p.map(t => t.id === dr.taskId ? { ...t, start_date: sd, end_date: ed, working_days: countWD(sd, ed) } : t));
    } else if (dr.type === 're') {
      const ed = timeline.dates[di]?.date;
      if (ed && ed >= dr.oS) setTasks(p => p.map(t => t.id === dr.taskId ? { ...t, end_date: ed, working_days: countWD(t.start_date, ed) } : t));
    } else if (dr.type === 'rs') {
      const sd = timeline.dates[di]?.date;
      if (sd && (!dr.oE || sd <= dr.oE)) setTasks(p => p.map(t => t.id === dr.taskId ? { ...t, start_date: sd, working_days: countWD(sd, t.end_date) } : t));
    } else if (dr.type === 'mv') {
      const delta = di - dr.ai;
      const ns = addDays(dr.oS, delta), ne = dr.oE ? addDays(dr.oE, delta) : ns;
      setTasks(p => p.map(t => t.id === dr.taskId ? { ...t, start_date: ns, end_date: ne } : t));
    } else if (dr.type === 'project') {
      // Shift ALL tasks by delta days
      const delta = di - dr.ai;
      if (delta === 0) return;
      dr.ai = di;
      setTasks(p => p.map(t => {
        if (!t.start_date) return t;
        return { ...t, start_date: addDays(t.start_date, delta), end_date: t.end_date ? addDays(t.end_date, delta) : null };
      }));
      setChart(c => ({
        ...c,
        project_start: c.project_start ? addDays(c.project_start, delta) : null,
        project_end: c.project_end ? addDays(c.project_end, delta) : null,
      }));
    }
  }, [timeline, dayWidth]);

  const handleMouseUp = useCallback(() => {
    if (dragRef.current) {
      // A drag just ended — recompute parent task dates from (possibly moved) children
      // then persist. Without this, parent start/end/progress stay stale after drag.
      setTasks(prev => {
        const computed = computeParentDates(prev);
        autoSave(computed);
        return computed;
      });
      dragRef.current = null;
    }
    if (colResizeRef.current) { colResizeRef.current = null; }
  }, [autoSave, computeParentDates]);

  useEffect(() => {
    const onMove = (e) => handleMouseMove(e.touches ? e.touches[0] : e);
    const onEnd = () => handleMouseUp();
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Scroll to today — ONCE per chart open. Don't snap back on edits / timeline recompute.
  const hasScrolledToTodayRef = useRef(false);
  useEffect(() => {
    if (loading || hasScrolledToTodayRef.current || !chartAreaRef.current) return;
    const idx = diffDays(timeline.startDate, toLocalDateStr(new Date()));
    if (idx > 0) chartAreaRef.current.scrollLeft = Math.max(0, (idx - 3) * dayWidth);
    hasScrolledToTodayRef.current = true;
  }, [loading, timeline, dayWidth]);

  if (loading) return <div className="text-center py-8 text-muted-foreground">Loading chart...</div>;
  if (!chart) return <div className="text-center py-8 text-muted-foreground">Chart not found</div>;

  const todayStr = toLocalDateStr(new Date());
  const todayIdx = diffDays(timeline.startDate, todayStr);
  const actW = 44;
  const innerLeftW = actW + colWidths.name + colWidths.start + colWidths.wd + colWidths.end + colWidths.pct;

  // Month groups
  const months = [];
  let cm = null;
  timeline.dates.forEach((d, i) => {
    const k = `${d.year}-${d.month}`;
    if (k !== cm) { months.push({ label: `${d.mn} ${d.year}`, days: 1 }); cm = k; }
    else months[months.length - 1].days++;
  });

  const ColResizer = ({ col }) => (
    <div
      className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary/60 z-10"
      onMouseDown={e => { e.preventDefault(); colResizeRef.current = { col, startX: e.clientX, startW: colWidths[col] }; }}
      onTouchStart={e => { const t = e.touches[0]; colResizeRef.current = { col, startX: t.clientX, startW: colWidths[col] }; }}
    />
  );

  const EditCell = ({ task, field, type, readOnly }) => {
    const isEditing = !readOnly && editingCell?.taskId === task.id && editingCell?.field === field;
    const raw = task[field];
    const display = field === 'start_date' || field === 'end_date' ? (raw ? raw.slice(5) : '-') : (raw ?? '-');

    if (isEditing) {
      return (
        <input
          autoFocus type={type || 'text'} value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onBlur={commitEdit} onKeyDown={e => e.key === 'Enter' && commitEdit()}
          className="w-full text-[8px] bg-transparent border-b border-primary outline-none text-center px-0.5"
          min={type === 'number' ? 0 : undefined} max={field === 'progress' ? 100 : undefined}
        />
      );
    }
    return (
      <span className={`text-[8px] text-muted-foreground block truncate px-0.5 ${readOnly ? 'opacity-60' : 'cursor-text'}`}
        onClick={readOnly ? undefined : () => startEdit(task.id, field, raw || '')}>
        {display}
      </span>
    );
  };

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onBack} className="h-8 px-2" data-testid="btn-back-gantt">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div>
            <h3 className="text-xs sm:text-sm font-bold text-foreground flex items-center gap-1.5">
              <BarChart3 className="w-3.5 h-3.5 text-primary" /> {chart.project_name}
            </h3>
            <div className="flex items-center gap-2 text-[8px] sm:text-[10px] text-muted-foreground mt-0.5">
              {chart.project_start && <span>Start: {chart.project_start}</span>}
              {chart.project_end && <span>End: {chart.project_end}</span>}
              {chart.project_working_days > 0 && <span>WD: {chart.project_working_days}</span>}
              <span>Progress: {chart.project_progress || 0}%</span>
              {saving && <span className="text-primary animate-pulse">Saving...</span>}
            </div>
          </div>
        </div>
        {/* Zoom controls + Calendar task button */}
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" className="h-7 text-[9px] px-2 mr-1 border-primary/40 text-primary hover:bg-primary/5" onClick={exportPdf} disabled={exporting} data-testid="btn-export-schedule-pdf">
            <FileDown className="w-3 h-3 mr-0.5" /> {exporting ? 'Exporting...' : 'Export PDF'}
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-[9px] px-2 mr-1 border-emerald-400/40 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30" onClick={openImport} data-testid="btn-import-bom-scope">
            <ListTree className="w-3 h-3 mr-0.5" /> Import Scope from BOM
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-[9px] px-2 mr-1 border-indigo-400/40 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/30" onClick={openMove} data-testid="btn-move-schedule">
            <CalendarClock className="w-3 h-3 mr-0.5" /> Move Entire Schedule
          </Button>
          {onCreateCalTask && (
            <Button variant="outline" size="sm" className="h-7 text-[9px] px-2 mr-1" onClick={onCreateCalTask} data-testid="btn-create-cal-task-gantt">
              <CalendarPlus className="w-3 h-3 mr-0.5" /> Calendar Task
            </Button>
          )}
          {onCreatePO && (
            <Button variant="outline" size="sm" className="h-7 text-[9px] px-2 mr-1 border-orange-400/40 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950/30" onClick={() => onCreatePO(chart)} data-testid="btn-create-po-gantt">
              <Package className="w-3 h-3 mr-0.5" /> New PO
            </Button>
          )}
          <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setDayWidth(w => Math.max(14, w - 6))} data-testid="btn-zoom-out">
            <ZoomOut className="w-3.5 h-3.5" />
          </Button>
          <span className="text-[8px] text-muted-foreground w-8 text-center">{dayWidth}px</span>
          <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setDayWidth(w => Math.min(60, w + 6))} data-testid="btn-zoom-in">
            <ZoomIn className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Gantt area */}
      <div className="border border-border/40 rounded-lg overflow-hidden bg-card/50">
        <div className="flex" style={{ minHeight: HEADER_HEIGHT + (visibleTasks.length + 3) * ROW_HEIGHT + 20 }}>

          {/* LEFT PANEL — 30% scrollable with resizable columns */}
          <div className="border-r border-border/40 bg-card/80 select-none overflow-x-auto" style={{ width: '30%', minWidth: 0 }}>
            <div style={{ minWidth: innerLeftW }}>
            {/* Column headers */}
            <div className="flex items-center border-b border-border/40 text-[8px] font-semibold text-muted-foreground uppercase tracking-wider" style={{ height: HEADER_HEIGHT }}>
              <div className="flex-shrink-0" style={{ width: actW }} />
              <div className="relative text-center flex-shrink-0" style={{ width: colWidths.name }}>Name<ColResizer col="name" /></div>
              <div className="relative text-center flex-shrink-0" style={{ width: colWidths.start }}>Start<ColResizer col="start" /></div>
              <div className="relative text-center flex-shrink-0" style={{ width: colWidths.wd }}>WD<ColResizer col="wd" /></div>
              <div className="relative text-center flex-shrink-0" style={{ width: colWidths.end }}>End<ColResizer col="end" /></div>
              <div className="relative text-center flex-shrink-0" style={{ width: colWidths.pct }}>%<ColResizer col="pct" /></div>
            </div>

            {/* Project summary row */}
            {chart.project_start && (
              <div className="flex items-center border-b-2 border-primary/30 bg-primary/5" style={{ height: ROW_HEIGHT }} data-testid="project-summary-row">
                <div className="flex-shrink-0" style={{ width: actW }} />
                <div className="min-w-0 overflow-hidden flex-shrink-0 px-1" style={{ width: colWidths.name }}>
                  <span className="text-[9px] font-bold text-primary truncate block">{chart.project_name}</span>
                </div>
                <div className="text-center flex-shrink-0" style={{ width: colWidths.start }}><span className="text-[8px] text-primary/70">{chart.project_start?.slice(5)}</span></div>
                <div className="text-center flex-shrink-0" style={{ width: colWidths.wd }}><span className="text-[8px] text-primary/70">{chart.project_working_days}</span></div>
                <div className="text-center flex-shrink-0" style={{ width: colWidths.end }}><span className="text-[8px] text-primary/70">{chart.project_end?.slice(5)}</span></div>
                <div className="text-center flex-shrink-0" style={{ width: colWidths.pct }}><span className="text-[8px] text-primary/70">{chart.project_progress}%</span></div>
              </div>
            )}

            {/* Task rows */}
            {visibleTasks.map((task) => {
              const idx = task._origIdx;
              const behind = isBehind(task);
              const parent = isParent(idx);
              const collapsed = collapsedIds.has(task.id);
              return (
                <div key={task.id}
                  className={`flex items-center border-b border-border/20 hover:bg-muted/20 ${behind ? 'bg-red-500/5' : ''} ${dragIdx === idx ? 'opacity-40' : ''} ${dropIdx === idx ? 'border-t-2 border-t-primary' : ''}`}
                  style={{ height: ROW_HEIGHT }}
                  data-testid={`task-row-${task.id}`}
                  draggable
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDrop={() => handleDrop(idx)}
                  onDragEnd={handleDragEnd}
                >
                  {/* Drag handle + indent/outdent + delete */}
                  <div className="flex items-center gap-0 flex-shrink-0 justify-center" style={{ width: actW }}>
                    <span className="cursor-grab active:cursor-grabbing p-0.5 text-muted-foreground/40 hover:text-muted-foreground" data-testid={`drag-handle-${idx}`}><GripVertical className="w-2.5 h-2.5" /></span>
                    <button onClick={() => indentTask(idx)} className="p-0.5 hover:text-primary text-muted-foreground/60" data-testid={`btn-indent-${idx}`}><Indent className="w-2 h-2" /></button>
                    <button onClick={() => outdentTask(idx)} className="p-0.5 hover:text-primary text-muted-foreground/60" data-testid={`btn-outdent-${idx}`}><Outdent className="w-2 h-2" /></button>
                    <button onClick={(e) => { e.stopPropagation(); deleteTask(task.id); }} className="p-0.5 hover:text-destructive text-muted-foreground/50" data-testid={`btn-delete-task-${task.id}`}><Trash2 className="w-2 h-2" /></button>
                  </div>

                  {/* Name with collapse toggle for parents */}
                  <div className="min-w-0 overflow-hidden flex-shrink-0 flex items-center" style={{ width: colWidths.name, paddingLeft: task.level * 10 + 1 }}>
                    {parent ? (
                      <button onClick={() => toggleCollapse(task.id)} className="p-0 mr-0.5 text-muted-foreground/70 hover:text-primary flex-shrink-0" data-testid={`btn-toggle-${task.id}`}>
                        {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </button>
                    ) : (
                      <span className="w-3 mr-0.5 flex-shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      {editingCell?.taskId === task.id && editingCell?.field === 'name' ? (
                        <input autoFocus value={editValue} onChange={e => setEditValue(e.target.value)}
                          onBlur={() => { updateField(task.id, 'name', editValue); setEditingCell(null); }}
                          onKeyDown={e => { if(e.key==='Enter'){ updateField(task.id,'name',editValue); setEditingCell(null); }}}
                          className="w-full text-[9px] bg-transparent border-b border-primary outline-none py-0.5" />
                      ) : (
                        <span className={`text-[9px] truncate cursor-text block ${task.level===0?'font-semibold':''} ${behind?'text-red-500':''}`}
                          onClick={() => startEdit(task.id, 'name', task.name)}>{task.name}</span>
                      )}
                    </div>
                  </div>

                  <div className="text-center flex-shrink-0" style={{ width: colWidths.start }}><EditCell task={task} field="start_date" type="date" readOnly={parent} /></div>
                  <div className="text-center flex-shrink-0" style={{ width: colWidths.wd }}><EditCell task={task} field="working_days" type="number" readOnly={parent} /></div>
                  <div className="text-center flex-shrink-0" style={{ width: colWidths.end }}><EditCell task={task} field="end_date" type="date" readOnly={parent} /></div>
                  <div className="text-center flex-shrink-0" style={{ width: colWidths.pct }}><EditCell task={task} field="progress" type="number" readOnly={parent} /></div>
                </div>
              );
            })}

            {/* Add task row at bottom */}
            <div className="flex items-center border-b border-border/20" style={{ height: ROW_HEIGHT }}>
              <div className="flex-shrink-0 flex items-center justify-center" style={{ width: actW }}>
                <Plus className="w-2.5 h-2.5 text-muted-foreground/40" />
              </div>
              <div className="flex-shrink-0 px-1" style={{ width: colWidths.name }}>
                <input
                  value={newTaskName}
                  onChange={e => setNewTaskName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && newTaskName.trim()) addTask(newTaskName); }}
                  placeholder="Add task..."
                  className="w-full text-[9px] bg-transparent outline-none py-0.5 text-muted-foreground placeholder:text-muted-foreground/40"
                  data-testid="input-new-task"
                />
              </div>
              <div style={{ width: colWidths.start + colWidths.wd + colWidths.end + colWidths.pct }} className="flex items-center justify-center">
                <Button variant="ghost" size="sm" className="h-4 text-[8px] text-muted-foreground px-1" onClick={() => addTask(newTaskName)}
                  data-testid="btn-add-task">
                  <Plus className="w-3 h-3 mr-0.5" /> Add
                </Button>
              </div>
            </div>
            </div>
          </div>

          {/* RIGHT PANEL: Chart — 70% */}
          <div className="overflow-x-auto overflow-y-hidden" style={{ width: '70%' }} ref={chartAreaRef}>
            <div style={{ width: timeline.dates.length * dayWidth, position: 'relative' }}>
              {/* Month row */}
              <div className="flex border-b border-border/40" style={{ height: HEADER_HEIGHT / 2 }}>
                {months.map((m, i) => (
                  <div key={i} className="border-r border-border/20 text-[8px] font-semibold text-muted-foreground flex items-center justify-center"
                    style={{ width: m.days * dayWidth }}>{m.label}</div>
                ))}
              </div>
              {/* Day row */}
              <div className="flex border-b border-border/40" style={{ height: HEADER_HEIGHT / 2 }}>
                {timeline.dates.map((d, i) => (
                  <div key={i} className={`text-center text-[7px] border-r border-border/10 flex items-center justify-center
                    ${d.dow%6===0?'bg-muted/30 text-muted-foreground/50':'text-muted-foreground'}
                    ${d.date===todayStr?'bg-primary/20 text-primary font-bold':''}`}
                    style={{ width: dayWidth }}>{d.day}</div>
                ))}
              </div>

              {/* Project summary bar — TOP, draggable */}
              {chart.project_start && chart.project_end && (
                <div className="relative border-b-2 border-primary/30 bg-primary/5" style={{ height: ROW_HEIGHT }}
                  onMouseDown={e => {
                    if (e.button !== 0 || !chartAreaRef.current) return;
                    e.preventDefault();
                    const rect = chartAreaRef.current.getBoundingClientRect();
                    const x = e.clientX - rect.left + chartAreaRef.current.scrollLeft;
                    const di = Math.floor(x / dayWidth);
                    dragRef.current = { type: 'project', ai: di, origStart: chart.project_start };
                  }}
                  data-testid="project-bar-row"
                >
                  {(() => {
                    const psi = getIdx(chart.project_start), pei = getIdx(chart.project_end);
                    const pw = Math.max((pei - psi + 1) * dayWidth, dayWidth);
                    const pp = (chart.project_progress / 100) * pw;
                    return (
                      <div className="absolute top-0.5 rounded-sm bg-primary/20 cursor-grab active:cursor-grabbing" style={{ left: psi * dayWidth, width: pw, height: ROW_HEIGHT - 4 }}>
                        <div className="absolute top-0 left-0 bottom-0 rounded-sm bg-primary/60" style={{ width: pp }} />
                        <div className="absolute inset-0 flex items-center px-1.5 text-[8px] font-bold text-primary truncate" style={{ zIndex: 1 }}>
                          {chart.project_name} — {chart.project_progress}%
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Task Bars */}
              {visibleTasks.map((task) => {
                const has = task.start_date && task.end_date;
                const si = has ? getIdx(task.start_date) : 0;
                const ei = has ? getIdx(task.end_date) : 0;
                const bw = has ? Math.max((ei - si + 1) * dayWidth, dayWidth) : 0;
                const bl = si * dayWidth;
                const behind = isBehind(task);
                // Bar palette: light-blue background + dark-blue progress.
                // If behind schedule, shift to red shades.
                const barBg = behind ? '#fecaca' : '#bfdbfe';      // light red / light blue
                const barFill = behind ? '#dc2626' : '#2563eb';    // dark red  / dark blue
                const pw = has ? (task.progress / 100) * bw : 0;

                return (
                  <div key={task.id} className="relative border-b border-border/10" style={{ height: ROW_HEIGHT }}
                    onMouseDown={e => handleChartMouseDown(e, task.id)} data-testid={`chart-row-${task.id}`}>
                    {/* Weekends */}
                    {timeline.dates.map((d, i) => d.dow % 6 === 0 && (
                      <div key={i} className="absolute top-0 bottom-0 bg-muted/15" style={{ left: i * dayWidth, width: dayWidth }} />
                    ))}
                    {/* Today line */}
                    {todayIdx >= 0 && todayIdx < timeline.dates.length && (
                      <div className="absolute top-0 bottom-0 w-px bg-primary/40" style={{ left: todayIdx * dayWidth + dayWidth / 2 }} />
                    )}
                    {/* Bar */}
                    {has && (
                      <div className="absolute top-0.5 rounded-sm cursor-grab active:cursor-grabbing"
                        style={{ left: bl, width: bw, height: ROW_HEIGHT - 4, backgroundColor: barBg }}>
                        <div className="absolute top-0 left-0 bottom-0 rounded-sm" style={{ width: pw, backgroundColor: barFill }} />
                        <div className="absolute inset-0 flex items-center px-1 text-[8px] font-medium text-foreground truncate" style={{ zIndex: 1 }}>
                          {task.name}{behind && <span className="ml-0.5 text-red-700 font-bold">!</span>}
                        </div>
                        <div className="absolute top-0 bottom-0 left-0 w-2 cursor-col-resize" style={{ zIndex: 2 }} />
                        <div className="absolute top-0 bottom-0 right-0 w-2 cursor-col-resize" style={{ zIndex: 2 }} />
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Add task row (empty) */}
              <div className="relative" style={{ height: ROW_HEIGHT }} />
            </div>
          </div>
        </div>
      </div>

      {importOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="gantt-import-modal" onClick={() => !importing && setImportOpen(false)}>
          <div className="bg-background rounded-lg shadow-xl w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()}>
            <div>
              <h3 className="text-base font-bold flex items-center gap-2"><ListTree className="w-4 h-4 text-emerald-600" /> Import Scope of Works from BOM</h3>
              <p className="text-xs text-muted-foreground mt-1">Pulls the selected project's BOM as schedule tasks following the hierarchy: Category → Section → Task (sub-items excluded). Tasks come in without dates for you to schedule.</p>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold">Source project</label>
              <select value={importSource} onChange={e => setImportSource(e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="gantt-import-source">
                <option value="">Choose a project...</option>
                {importProjects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
              </select>
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={() => setImportOpen(false)} disabled={importing} data-testid="gantt-import-cancel">Cancel</Button>
              <Button variant="outline" size="sm" className="border-amber-400/50 text-amber-700 hover:bg-amber-50" onClick={() => doImport('replace')} disabled={!importSource || importing} data-testid="gantt-import-replace">Replace schedule</Button>
              <Button size="sm" onClick={() => doImport('append')} disabled={!importSource || importing} data-testid="gantt-import-append">{importing ? 'Importing...' : 'Append to schedule'}</Button>
            </div>
          </div>
        </div>
      )}

      {moveOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="gantt-move-modal" onClick={() => !moving && setMoveOpen(false)}>
          <div className="bg-background rounded-lg shadow-xl w-full max-w-sm p-5 space-y-4" onClick={e => e.stopPropagation()}>
            <div>
              <h3 className="text-base font-bold flex items-center gap-2"><CalendarClock className="w-4 h-4 text-indigo-600" /> Move Entire Schedule</h3>
              <p className="text-xs text-muted-foreground mt-1">Pick the new start date for the whole schedule. Every task shifts by the same number of days, keeping their relative placement and durations.</p>
            </div>
            {scheduleStart ? (
              <p className="text-xs text-muted-foreground">Currently starts on <span className="font-semibold text-foreground">{scheduleStart}</span></p>
            ) : (
              <p className="text-xs text-amber-600">No task has dates yet — add dates before moving the schedule.</p>
            )}
            <div className="flex justify-center border rounded-md" data-testid="gantt-move-calendar">
              <Calendar mode="single" selected={moveDate} onSelect={setMoveDate} defaultMonth={moveDate || undefined} initialFocus />
            </div>
            {moveDate && (
              <p className="text-xs text-center">New start date: <span className="font-semibold">{toLocalDateStr(moveDate)}</span></p>
            )}
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={() => setMoveOpen(false)} disabled={moving} data-testid="gantt-move-cancel">Cancel</Button>
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={moveSchedule} disabled={!moveDate || !scheduleStart || moving} data-testid="gantt-move-confirm">
                {moving ? 'Moving...' : 'Move Schedule'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
